import React, { useEffect, useState } from "react";
import { Loader2, Plug, Save, ShieldCheck, Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { NumberInput } from "./NumberInput";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { GmailConnect } from "./GmailConnect";

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-bold uppercase tracking-widest">{label}</Label>
    {children}
    {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
  </div>
);

export const FelConfig = () => {
  const { user } = useAuth();
  const canEdit = !!user && (user.is_admin || user.role_name === "Dirección General");
  const [cfg, setCfg] = useState(null);
  const [pwdTest, setPwdTest] = useState("");
  const [pwdProd, setPwdProd] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/fel/config").then((r) => setCfg({
      environment: "test", base_url_test: "https://testnucgt.digifact.com/api", base_url_prod: "https://nucgt.digifact.com/gt.com.apinuc/api",
      nit_test: "", username_test: "", nit_prod: "", username_prod: "",
      establishment: 1, default_currency: "GTQ", emisor_nombre: "", emisor_comercial: "",
      emisor_direccion: "", emisor_municipio: "Guatemala", emisor_departamento: "Guatemala",
      emisor_codigo_postal: "01001", emisor_pais: "GT",
      afiliacion_iva: "GEN", iva_rate: 12, frases: [{ tipo: 1, escenario: 1 }], ...r.data,
    })).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setFrase = (i, k, v) => setCfg((c) => { const f = [...(c.frases || [])]; f[i] = { ...f[i], [k]: Number(v) }; return { ...c, frases: f }; });
  const addFrase = () => setCfg((c) => ({ ...c, frases: [...(c.frases || []), { tipo: 2, escenario: 1 }] }));
  const delFrase = (i) => setCfg((c) => ({ ...c, frases: c.frases.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      if (pwdTest) payload.password_test = pwdTest;
      if (pwdProd) payload.password_prod = pwdProd;
      const r = await api.put("/fel/config", payload);
      setCfg((c) => ({ ...c, ...r.data })); setPwdTest(""); setPwdProd("");
      toast.success("Configuración FEL guardada");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const testConn = async () => {
    setTesting(true);
    try { const r = await api.post("/fel/test-connection"); toast.success(r.data?.message || "Conexión exitosa"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  if (loading || !cfg) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="fel-config">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading font-bold text-lg">Configuración FEL · DIGIFACT</h3>
          <p className="text-xs text-muted-foreground">Credenciales del certificador para emitir Facturas Electrónicas en Línea.</p>
        </div>
        {cfg.configured
          ? <Badge className="rounded-sm bg-green-100 text-green-700 gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Configurado ({cfg.environment === "prod" ? "Producción" : "Pruebas"})</Badge>
          : <Badge className="rounded-sm bg-amber-100 text-amber-700">Pendiente de credenciales</Badge>}
      </div>

      <GmailConnect />

      {!canEdit && <p className="text-xs text-muted-foreground border border-border rounded-sm p-3">Solo la Dirección General puede modificar esta configuración.</p>}

      <Card className="rounded-sm border shadow-none p-5 space-y-4">
        <p className="text-xs font-bold uppercase tracking-widest text-primary">Conexión DIGIFACT</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Ambiente">
            <Select value={cfg.environment} onValueChange={(v) => set("environment", v)} disabled={!canEdit}><SelectTrigger className="rounded-sm" data-testid="fel-env"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="test">Pruebas (Sandbox)</SelectItem><SelectItem value="prod">Producción</SelectItem></SelectContent></Select>
          </Field>
          <Field label="Moneda por defecto">
            <Select value={cfg.default_currency} onValueChange={(v) => set("default_currency", v)} disabled={!canEdit}><SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="GTQ">GTQ</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent></Select>
          </Field>
          <Field label="URL API Pruebas" hint="Sandbox NUC: https://testnucgt.digifact.com/api"><Input className="rounded-sm" value={cfg.base_url_test} onChange={(e) => set("base_url_test", e.target.value)} disabled={!canEdit} data-testid="fel-url-test" /></Field>
          <Field label="URL API Producción" hint="Prod NUC: https://nucgt.digifact.com/gt.com.apinuc/api"><Input className="rounded-sm" value={cfg.base_url_prod} onChange={(e) => set("base_url_prod", e.target.value)} disabled={!canEdit} data-testid="fel-url-prod" /></Field>
          <Field label="Código establecimiento"><NumberInput className="rounded-sm" value={cfg.establishment} onValueChange={(v) => set("establishment", v)} disabled={!canEdit} data-testid="fel-establishment" /></Field>
          <div className="sm:col-span-2 grid sm:grid-cols-2 gap-4">
            <div className="space-y-3 border border-border rounded-sm p-3">
              <p className="text-[11px] font-bold uppercase tracking-widest text-amber-600">Credenciales · Pruebas (Sandbox)</p>
              <Field label="NIT emisor"><Input className="rounded-sm" value={cfg.nit_test} onChange={(e) => set("nit_test", e.target.value)} disabled={!canEdit} data-testid="fel-nit-test" /></Field>
              <Field label="Usuario FEL"><Input className="rounded-sm" value={cfg.username_test} onChange={(e) => set("username_test", e.target.value)} disabled={!canEdit} data-testid="fel-username-test" /></Field>
              <Field label="Contraseña" hint={cfg.has_password_test ? "Ya guardada. Escribe una nueva solo si deseas cambiarla." : "Se guardará encriptada."}>
                <Input type="password" className="rounded-sm" value={pwdTest} onChange={(e) => setPwdTest(e.target.value)} placeholder={cfg.has_password_test ? "••••••••" : ""} disabled={!canEdit} data-testid="fel-password-test" />
              </Field>
            </div>
            <div className="space-y-3 border border-border rounded-sm p-3">
              <p className="text-[11px] font-bold uppercase tracking-widest text-green-700">Credenciales · Producción</p>
              <Field label="NIT emisor"><Input className="rounded-sm" value={cfg.nit_prod} onChange={(e) => set("nit_prod", e.target.value)} disabled={!canEdit} data-testid="fel-nit-prod" /></Field>
              <Field label="Usuario FEL"><Input className="rounded-sm" value={cfg.username_prod} onChange={(e) => set("username_prod", e.target.value)} disabled={!canEdit} data-testid="fel-username-prod" /></Field>
              <Field label="Contraseña" hint={cfg.has_password_prod ? "Ya guardada. Escribe una nueva solo si deseas cambiarla." : "Se guardará encriptada."}>
                <Input type="password" className="rounded-sm" value={pwdProd} onChange={(e) => setPwdProd(e.target.value)} placeholder={cfg.has_password_prod ? "••••••••" : ""} disabled={!canEdit} data-testid="fel-password-prod" />
              </Field>
            </div>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm border shadow-none p-5 space-y-4">
        <p className="text-xs font-bold uppercase tracking-widest text-primary">Datos del emisor</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Razón social"><Input className="rounded-sm" value={cfg.emisor_nombre} onChange={(e) => set("emisor_nombre", e.target.value)} disabled={!canEdit} data-testid="fel-emisor-nombre" /></Field>
          <Field label="Nombre comercial"><Input className="rounded-sm" value={cfg.emisor_comercial} onChange={(e) => set("emisor_comercial", e.target.value)} disabled={!canEdit} /></Field>
          <div className="sm:col-span-2"><Field label="Dirección" hint="Dirección fiscal COMPLETA tal como está registrada en SAT. Esto es lo que aparece en la factura y en el XML."><Textarea rows={2} className="rounded-sm" value={cfg.emisor_direccion} onChange={(e) => set("emisor_direccion", e.target.value)} disabled={!canEdit} data-testid="fel-emisor-direccion" /></Field></div>
          <Field label="Municipio" hint="Debe coincidir con el registro SAT (ej. Mixco, Guatemala)."><Input className="rounded-sm" value={cfg.emisor_municipio || ""} onChange={(e) => set("emisor_municipio", e.target.value)} disabled={!canEdit} data-testid="fel-emisor-municipio" /></Field>
          <Field label="Departamento" hint="Ej. Guatemala"><Input className="rounded-sm" value={cfg.emisor_departamento || ""} onChange={(e) => set("emisor_departamento", e.target.value)} disabled={!canEdit} data-testid="fel-emisor-departamento" /></Field>
          <Field label="Código postal" hint="Ej. 01057 (según municipio/zona)"><Input className="rounded-sm" value={cfg.emisor_codigo_postal || ""} onChange={(e) => set("emisor_codigo_postal", e.target.value)} disabled={!canEdit} data-testid="fel-emisor-cp" /></Field>
          <Field label="País" hint="Código ISO, ej. GT"><Input className="rounded-sm" value={cfg.emisor_pais || "GT"} onChange={(e) => set("emisor_pais", e.target.value)} disabled={!canEdit} data-testid="fel-emisor-pais" /></Field>
          <Field label="Afiliación IVA">
            <Select value={cfg.afiliacion_iva} onValueChange={(v) => set("afiliacion_iva", v)} disabled={!canEdit}><SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="GEN">IVA General</SelectItem><SelectItem value="PEQ">Pequeño Contribuyente</SelectItem><SelectItem value="EXE">Exento</SelectItem></SelectContent></Select>
          </Field>
          <Field label="Tasa IVA (%)"><NumberInput step="0.1" className="rounded-sm" value={cfg.iva_rate} onValueChange={(v) => set("iva_rate", v)} disabled={!canEdit} /></Field>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-bold uppercase tracking-widest">Frases fiscales</Label>
            {canEdit && <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs gap-1" onClick={addFrase} data-testid="fel-add-frase"><Plus className="h-3 w-3" /> Agregar</Button>}
          </div>
          {(cfg.frases || []).length === 0 && <p className="text-[10px] text-red-600">Debes configurar al menos una Frase fiscal según tu régimen SAT (p. ej. Tipo 1 / Escenario 1). Es obligatoria para poder certificar en DIGIFACT.</p>}
          {(cfg.frases || []).map((f, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input type="number" className="rounded-sm h-8 w-28 text-xs" value={f.tipo} onChange={(e) => setFrase(i, "tipo", e.target.value)} disabled={!canEdit} placeholder="Tipo" />
              <Input type="number" className="rounded-sm h-8 w-32 text-xs" value={f.escenario} onChange={(e) => setFrase(i, "escenario", e.target.value)} disabled={!canEdit} placeholder="Escenario" />
              {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => delFrase(i)}><Trash2 className="h-3.5 w-3.5" /></Button>}
            </div>
          ))}
        </div>
      </Card>

      {canEdit && (
        <div className="flex gap-2">
          <Button className="rounded-sm gap-2 font-bold" onClick={save} disabled={saving} data-testid="fel-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar</Button>
          <Button variant="outline" className="rounded-sm gap-2" onClick={testConn} disabled={testing || !cfg.configured} data-testid="fel-test-btn">{testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Probar conexión</Button>
        </div>
      )}
    </div>
  );
};

export default FelConfig;
